"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, Check, Info, ShieldCheck, MapPin, Layers, Zap, Maximize } from "lucide-react";
import { cn } from "@/lib/utils";
const CORE_PACKAGES = [{
  id: "essential",
  name: "Essential Flythrough",
  description: "Best for municipal and daily-fee courses wanting professional aerial visibility."
}, {
  id: "cinematic",
  name: "Cinematic Club Showcase",
  description: "Best for courses looking to elevate branding and marketing visuals."
}, {
  id: "full-experience",
  name: "Full Course Experience",
  description: "Best for courses wanting a complete cinematic media package.",
  isPopular: true
}, {
  id: "custom",
  name: "Custom Vision",
  description: "For golf courses wanting a completely custom media plan tailored to their goals and budget."
}];
const TRUST_INDICATORS = [{
  icon: ShieldCheck,
  text: "FAA Part 107 Certified"
}, {
  icon: MapPin,
  text: "Arizona Based"
}, {
  icon: Layers,
  text: "Flexible Packages"
}, {
  icon: Zap,
  text: "Fast Turnaround"
}, {
  icon: Maximize,
  text: "Courses of All Sizes"
}];
function QuoteBuilderInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  if (isSubmitted && !submitError) {
    return <div className="p-12 text-center bg-primary/10 rounded-lg border border-secondary/30 animate-in fade-in zoom-in duration-500">
                <div className="w-16 h-16 bg-secondary/20 rounded-lg flex items-center justify-center mx-auto mb-6">
                    <Check className="text-secondary size-8" />
                </div>
                <h3 className="text-3xl font-bold text-foreground mb-4 font-serif">Proposal Request Received</h3>
                <p className="text-muted-foreground font-default max-w-md mx-auto mb-8">{successMessage}</p>
                <Button onClick={() => {
        resetForm();
        setSelectedPackage(null);
      }} variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-primary">
                    Start New Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-12">
            {}
            <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-secondary/20 pb-2">
                    <div className="bg-secondary/10 p-2 rounded-md">
                        <Layers className="size-5 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold font-serif text-foreground">1. Select a Starting Package</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CORE_PACKAGES.map((pkg, i) => <div key={pkg.id} data-index={i} onClick={() => setSelectedPackage(selectedPackage === pkg.id ? null : pkg.id)} className={cn("cursor-pointer p-6 rounded-lg border transition-all duration-300 flex flex-col justify-between group relative", selectedPackage === pkg.id ? "bg-secondary/10 border-secondary shadow-lg shadow-secondary/5" : "bg-card/30 border-border hover:border-secondary/50")}>
                            {pkg.isPopular && <div className="absolute -top-3 right-4 bg-secondary text-primary text-[10px] font-bold px-3 py-1 rounded-lg uppercase tracking-widest shadow-lg">
                                    Most Popular
                                </div>}
                            <div>
                                <div className="flex justify-between items-start mb-2">
                                    <span className={cn("text-sm font-bold tracking-widest uppercase", selectedPackage === pkg.id ? "text-secondary" : "text-muted-foreground")}>
                                        Package
                                    </span>
                                    {selectedPackage === pkg.id && <Check className="size-4 text-secondary" />}
                                </div>
                                <h4 className="text-xl font-bold text-foreground mb-2">{pkg.name}</h4>
                                <p className="text-sm text-muted-foreground leading-relaxed">{pkg.description}</p>
                            </div>
                        </div>)}
                </div>
            </div>

            {}
            <div className="space-y-8 pt-8">
                <div className="flex items-center gap-3 border-b border-secondary/20 pb-2">
                    <div className="bg-secondary/10 p-2 rounded-md">
                        <Info className="size-5 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold font-serif text-foreground">2. Tell Us About Your Course</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-foreground font-bold">Full Name</FormLabel>
                                <FormControl>
                                    <Input placeholder="John Smith" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^\S+@\S+$/i,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-foreground font-bold">Email Address</FormLabel>
                                <FormControl>
                                    <Input type="email" placeholder="jsmith@club.com" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="phone" rules={{
          required: "Phone is required"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-foreground font-bold">Phone Number</FormLabel>
                                <FormControl>
                                    <Input type="tel" placeholder="(555) 000-0000" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="courseName" rules={{
          required: "Course name is required"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-foreground font-bold">Golf Course / Club Name</FormLabel>
                                <FormControl>
                                    <Input placeholder="The Royal Links" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="location" rules={{
          required: "Location is required"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-foreground font-bold">Location (City, State)</FormLabel>
                                <FormControl>
                                    <Input placeholder="Scottsdale, AZ" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="timeline" rules={{
          required: "Timeline is required"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-foreground font-bold">Preferred Filming Timeline</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="bg-transparent border-0 border-b border-border focus:ring-0 focus:border-secondary transition-all w-full">
                                            <SelectValue placeholder="Select timeline" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        <SelectItem value="asap">As soon as possible</SelectItem>
                                        <SelectItem value="1-3-months">Within 1-3 months</SelectItem>
                                        <SelectItem value="seasonal">Next Season</SelectItem>
                                        <SelectItem value="flexible">Flexible / TBD</SelectItem>
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>} />
                </div>

                <FormField control={control} name="courseDescription" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-bold">Describe Your Course & Vision</FormLabel>
                            <FormControl>
                                <textarea {...field} placeholder="Describe your course, goals, current marketing, or what you would love golfers to experience when they see your course online." className="w-full min-h-[150px] bg-transparent border-0 border-b border-border focus:ring-0 focus:border-secondary transition-all outline-none py-2 resize-none text-base" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="notes" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-bold">Additional Project Notes (Optional)</FormLabel>
                            <FormControl>
                                <textarea {...field} placeholder="Tell us about specific holes, unique features, or custom requirements..." className="w-full min-h-[100px] bg-transparent border-0 border-b border-border focus:ring-0 focus:border-secondary transition-all outline-none py-2 resize-none" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <input type="hidden" name="selectedPackage" value={selectedPackage || ""} />

                {}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 py-8 border-t border-border/50">
                    {TRUST_INDICATORS.map((item, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center gap-2">
                            <item.icon className="size-5 text-secondary" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{item.text}</span>
                        </div>)}
                </div>

                {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                        {submitError}
                    </div>}

                <div className="pt-6 text-center">
                    <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[350px] py-7 rounded-lg bg-secondary hover:bg-secondary/90 text-primary font-bold text-xl transition-all shadow-xl">
                        {isSubmitting ? <>
                                <Loader className="mr-2 h-5 w-5 animate-spin" />
                                Processing Request...
                            </> : "Request My Custom Proposal"}
                    </Button>
                    <p className="mt-6 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
                        Our team will review your details and prepare a tailored media strategy for your course. Expect a response within 24 hours.
                    </p>
                </div>
            </div>
        </Form>;
}
export default function LeadIntakeFormSection() {
  return <section data-section-id="259403" id="quote-builder" className="py-20 px-4 !bg-[rgba(28,50,37,1)] md:py-32">
            <div className="max-w-[1000px] mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-6xl font-bold text-foreground font-serif tracking-tight mb-4">
                        Let's Showcase Your Golf Course
                    </h2>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
                        Tell us about your course, goals, and vision — and we'll build a professional aerial media package tailored to your needs and budget.
                    </p>
                </div>

                <FormProvider formKey="quote_form_request_a_quote" formId="quote_form_request_a_quote" sectionName="Quote Builder" className="bg-card/40 backdrop-blur-md p-6 md:p-12 rounded-xl border border-border shadow-2xl" defaultValues={{
        name: "",
        email: "",
        phone: "",
        courseName: "",
        location: "",
        timeline: "flexible",
        courseDescription: "",
        notes: "",
        selectedPackage: ""
      }} successMessage="Thank you for your interest. Our Arizona golf media specialists will review your vision and contact you within 24 hours with a formal proposal.">
                    <QuoteBuilderInner />
                </FormProvider>
            </div>
        </section>;
}